import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import {
	OpenIdConnectAction,
	OpenIdConnectCacheService,
	OpenIdConnectConfigService,
	OpenIdConnectProviders,
	OpenIdConnectSelector,
	OpenIdConnectState,
} from "@obg/common.verification";
import { GtmEventAction, GtmLoginMethod } from "@obg/gtm.types/adaptive";
import { CORE_ROUTE, CoreState, DgaAuthTransactionAction } from "@obg/ngx.core";
import { DeviceConfigService } from "@obg/ngx.global";
import { TelemetryService } from "@obg/ngx.global.evo";
import { RouteSelector } from "@obg/ngx.router";
import { cprValidator } from "@obg/ngx.ui.common";
import { merge, Subscription } from "rxjs";
import { filter, take, tap } from "rxjs/operators";

import {
	COMMON_POKER_INCLIENT_RETURN_URL,
	CPR_CONTROL,
	loginErrorPrefix,
	OIDC_POKER_INCLIENT_RETURN_URL,
	SHOULD_REMEMBER_USER_CONTROL,
} from "./id-select.const";

@Component({
	selector: "obg-m-id-select-component",
	templateUrl: "./id-select.component.html",
	styleUrls: ["./id-select.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	providers: [OpenIdConnectCacheService],
})
export class IdSelectComponent implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-id-select") hostCssClass = true;

	formGroup: UntypedFormGroup;
	mitIdEnabled = false;
	mitIdInFocus = false;
	isBusy = false;
	errorCode: string | undefined;
	errorPrefix = loginErrorPrefix;
	lastUsedCpr = "";
	buttonLabel = "login.type.mit-id.login";

	data$$: Subscription | undefined;

	constructor(
		private formBuilder: UntypedFormBuilder,
		private openIdConnectCacheService: OpenIdConnectCacheService,
		private store: Store<CoreState & OpenIdConnectState>,
		private changeDetector: ChangeDetectorRef,
		private openIdConnectAction: OpenIdConnectAction,
		private oidcSelector: OpenIdConnectSelector,
		private dgaAuthTransactionAction: DgaAuthTransactionAction,
		private telemetryService: TelemetryService,
		private deviceConfigService: DeviceConfigService,
		private configService: OpenIdConnectConfigService
	) {
		this.mitIdEnabled = this.configService.getIsMitIdEnabled();
		this.formGroup = this.formBuilder.group({
			cprNumber: new UntypedFormControl("", {
				validators: [cprValidator],
			}),
			shouldRememberUser: false,
		});
	}

	ngOnInit() {
		const lastActiveUser$ = this.openIdConnectCacheService.getLastActiveUser$().pipe(
			filter((cpr) => !!cpr),
			take(1),
			tap((cpr) => {
				this.formGroup.patchValue({
					shouldRememberUser: true,
					cprNumber: cpr,
				});
				this.mitIdInFocus = true;
			})
		);

		const setReturnUrl$ = this.store.pipe(
			RouteSelector.getCurrent$,
			filter((currentRoute) => this.deviceConfigService.isDesktop() || currentRoute?.queryParams?.returnUrl),
			tap((currentRoute) => {
				if (currentRoute?.queryParams?.returnUrl) {
					this.openIdConnectCacheService.setReturnUrl(currentRoute?.queryParams?.returnUrl);
					return;
				}

				// This route is only hit when we open the mitID login screen within any DGA brand poker-client
				if (currentRoute.pathName === COMMON_POKER_INCLIENT_RETURN_URL) {
					this.openIdConnectCacheService.setReturnUrl(OIDC_POKER_INCLIENT_RETURN_URL);
					return;
				}

				this.openIdConnectCacheService.setReturnUrl(currentRoute.pathName);
			})
		);

		const isRegisterDialog$ = this.store.pipe(
			RouteSelector.getCurrent$,
			filter(
				(currentRoute) =>
					currentRoute?.name === CORE_ROUTE.auth.registrationOidc || !!currentRoute?.queryParams?.isRegister
			),
			tap(() => {
				this.buttonLabel = "login.type.mit-id.register";
			})
		);

		const error$ = this.store.pipe(
			select(this.oidcSelector.getError()),
			filter((error) => !!error),
			take(1),
			tap((error) => (this.errorCode = error))
		);

		this.data$$ = merge(setReturnUrl$, lastActiveUser$, isRegisterDialog$, error$).subscribe(() =>
			this.changeDetector.markForCheck()
		);
	}

	onSubmit(): void {
		const cprFormValue = this.formGroup.get(CPR_CONTROL)?.value.replace(/[-+\s]/g, "") || "";

		if (!this.formGroup || this.formGroup.invalid || !cprFormValue) {
			this.formGroup.get("cprNumber")?.markAsTouched();
			this.formGroup.get("cprNumber")?.updateValueAndValidity();
			return;
		}

		this.isBusy = true;

		this.setRememberMeData(cprFormValue);
		this.openIdConnectCacheService.setRegistrationCpr(cprFormValue);

		this.telemetryService.track({
			RegistrationType: GtmLoginMethod.MitID,
			LoginMethod: GtmLoginMethod.MitID,
			event: GtmEventAction.MitIDOpen,
		});

		this.store.dispatch(
			this.dgaAuthTransactionAction.initializeOidcAuthTransaction({
				provider: OpenIdConnectProviders.MitId,
				cpr: cprFormValue,
			})
		);
	}

	onFocused(isFocused: any): void {
		this.mitIdInFocus = isFocused;
	}

	cancel(): void {
		this.formGroup.get(CPR_CONTROL)?.setValue("");
		this.mitIdInFocus = false;
		this.isBusy = false;
		this.formGroup.markAsUntouched();
	}

	uiInteractionFieldEvent(field: string) {
		this.telemetryService.track({
			event: GtmEventAction.LoginFieldChange,
			FieldName: field,
			LoginMethod: GtmLoginMethod.MitID,
		});
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.isBusy = false;
		if (this.errorCode) {
			this.store.dispatch(this.openIdConnectAction.setOpenIdConnectError({ error: undefined }));
		}
	}

	private setRememberMeData(cprFormValue: string): void {
		const shouldRememberUser = this.formGroup.get(SHOULD_REMEMBER_USER_CONTROL)?.value;
		const now = new Date();
		const inHalfHour = now.setMinutes(now.getMinutes() + 30);

		this.openIdConnectCacheService.setShouldRememberUserData({
			shouldRememberUser,
			cpr: shouldRememberUser ? cprFormValue : undefined,
			expiry: inHalfHour,
		});
	}
}
